import {IconCheck, IconClock, IconX} from "@tabler/icons-react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {useBalanceStore} from "@/store/useBalance.store";
import useCreateTicket from "../../../../private/Lottery/hooks/useCreateTicket";
import {transformDrawsToSorteos} from "../../../../private/Lottery/utils/lotteryDataTransformers";
import {getVenezuelanTime, isDrawPassed} from "../../../../private/Lottery/utils/timeUtils";
import styles from "./QuickBuyModal.module.css";

const DEFAULT_MIN_BET = 15;
const DEFAULT_MAX_BET = 45;
const CLOSE_MS = 150;
const COUNTDOWN_INTERVAL = 1000;
const URGENT_THRESHOLD_MIN = 5;
const LOCAL_ICON_PATH = "/src/assets/images/lottery-icons";

const getNextValidDraw = draws => {
  const sorteos = transformDrawsToSorteos(draws || []);
  for (const s of sorteos) {
    if (!isDrawPassed(s.hora, s.closeTime)) return s;
  }
  return null;
};

const getCountdownMs = closeTime => {
  if (!closeTime) return null;
  const match = closeTime.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const [, h, m, s = "0"] = match;
  const now = getVenezuelanTime();
  const close = new Date(now);
  close.setHours(Number(h), Number(m), Number(s), 0);
  const diff = close.getTime() - now.getTime();
  return diff > 0 ? diff : 0;
};

const formatCountdown = ms => {
  if (ms == null || ms <= 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return `${hours}h ${String(minutes).padStart(2, "0")}min ${String(seconds).padStart(2, "0")}s`;
};

const QuickBuyModal = ({card, game, onClose}) => {
  const {t} = useTranslation();
  const [closing, setClosing] = useState(false);
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [countdownMs, setCountdownMs] = useState(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const balance = useBalanceStore(state => state.getBalance);

  const {postCreateTicket, isPending, isSuccess, isError} = useCreateTicket();

  const minBet = game?.min_bet ? game.min_bet / 100 : DEFAULT_MIN_BET;
  const maxBet = game?.max_bet ? game.max_bet / 100 : DEFAULT_MAX_BET;

  const amountChips = [20, 50, 100, 200, 400];

  const validDraw = useMemo(() => getNextValidDraw(game?.draws), [game?.draws]);

  const drawExpired = useMemo(() => {
    if (!validDraw) return true;
    return countdownMs != null && countdownMs <= 0;
  }, [validDraw, countdownMs]);

  // Countdown timer
  useEffect(() => {
    if (!validDraw?.closeTime) return;
    setCountdownMs(getCountdownMs(validDraw.closeTime));

    const id = setInterval(() => {
      const remaining = getCountdownMs(validDraw.closeTime);
      setCountdownMs(remaining);
      if (remaining <= 0) clearInterval(id);
    }, COUNTDOWN_INTERVAL);

    return () => clearInterval(id);
  }, [validDraw?.closeTime]);

  // Handle mutation success
  useEffect(() => {
    if (isSuccess && submitted) {
      setSuccess(true);
    }
  }, [isSuccess, submitted]);

  // Handle mutation error
  useEffect(() => {
    if (isError && submitted) {
      setSubmitted(false);
    }
  }, [isError, submitted]);

  // Escape key
  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") startClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const startClose = useCallback(() => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => onClose(), CLOSE_MS);
  }, [closing, onClose]);

  const handleBackdropClick = e => {
    if (e.target === e.currentTarget) startClose();
  };

  const handleAmountChange = e => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    setAmount(val);
    setAmountError("");
  };

  const handleChipClick = value => {
    setAmount(String(value));
    setAmountError("");
  };

  const validateAmount = () => {
    const num = Number(amount);
    if (!amount || num <= 0) {
      setAmountError(t("qbErrorAmount", "Ingresa un monto válido"));
      return false;
    }
    if (num < minBet) {
      setAmountError(t("qbErrorMinBet", {defaultValue: "Mínimo {{min}} Bs", min: minBet}));
      return false;
    }
    if (num > maxBet) {
      setAmountError(t("qbErrorMaxBet", {defaultValue: "Máximo {{max}} Bs", max: maxBet}));
      return false;
    }
    if (balance != null && num > balance) {
      setAmountError(t("qbErrorBalance", "Saldo insuficiente"));
      return false;
    }
    return true;
  };

  const handleConfirm = () => {
    if (submitted || isPending || drawExpired) return;

    // Re-check draw validity at confirmation time
    const freshDraw = getNextValidDraw(game?.draws);
    if (!freshDraw) {
      setAmountError(t("qbErrorDrawClosed", "Sorteo cerrado"));
      return;
    }

    if (!validateAmount()) return;

    setSubmitted(true);

    const payload = {
      currency: "VES",
      items: [
        {
          draw_id: freshDraw.id,
          bets: [
            {
              numbers: [],
              amount: Number(amount),
              sign_id: String(card.number),
            },
          ],
        },
      ],
    };

    postCreateTicket(payload);
  };

  if (!card || !game) return null;

  const countdownStr = formatCountdown(countdownMs);
  const isUrgent = countdownMs != null && countdownMs > 0 && countdownMs < URGENT_THRESHOLD_MIN * 60 * 1000;
  const numericAmount = Number(amount);
  const canConfirm = numericAmount > 0 && !drawExpired && !submitted && !isPending;

  // ── Success state ──
  if (success) {
    return createPortal(
      <div
        className={`${styles.overlay} ${closing ? styles.closing : ""}`}
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.panel}>
          <div className={styles.handleBar} />
          <div className={styles.successWrap}>
            <div className={styles.successIcon}>
              <IconCheck
                size={28}
                stroke={2.5}
              />
            </div>
            <span className={styles.successTitle}>{t("qbSuccessTitle", "¡Compra exitosa!")}</span>
            <span className={styles.successDetail}>
              {card.displayName} #{String(card.number).padStart(2, "0")} — {card.gameName}
            </span>
            <span className={styles.successAmount}>Bs. {numericAmount.toLocaleString("es-VE")}</span>
            <button
              type="button"
              className={styles.successClose}
              onClick={startClose}
            >
              {t("qbClose", "Cerrar")}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // ── Purchase form state ──
  return createPortal(
    <div
      className={`${styles.overlay} ${closing ? styles.closing : ""}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={t("qbAriaLabel", "Compra rápida de animalito")}
    >
      <div
        className={styles.panel}
        ref={panelRef}
      >
        <div className={styles.handleBar} />
        <button
          type="button"
          className={styles.closeBtn}
          onClick={startClose}
          aria-label="Close"
        >
          <IconX
            size={16}
            stroke={2}
          />
        </button>

        {/* Hero */}
        <div className={styles.hero}>
          <div className={styles.heroImageWrap}>
            <img
              src={card.imageUrl}
              alt={card.displayName}
              className={styles.heroImage}
              onError={e => {
                e.target.onerror = null;
                e.target.src = `${LOCAL_ICON_PATH}/${card.name}.svg`;
              }}
            />
          </div>
          <div className={styles.heroInfo}>
            <span className={styles.heroNumber}>#{String(card.number).padStart(2, "0")}</span>
            <span className={styles.heroName}>{card.displayName}</span>
            <span className={styles.heroLottery}>{card.gameName}</span>
          </div>
        </div>

        {/* Draw info */}
        <div className={styles.drawRow}>
          <div className={styles.drawLabel}>
            <IconClock
              size={14}
              stroke={2}
            />
            {validDraw ? (
              <>
                <span>{t("qbNextDraw", "Sorteo")}</span>
                <span className={styles.drawTime}>{validDraw.hora}</span>
              </>
            ) : (
              <span className={styles.drawClosed}>{t("qbNoDraws", "Sin sorteos disponibles")}</span>
            )}
          </div>
          {validDraw && countdownStr && (
            <span className={`${styles.countdown} ${isUrgent ? styles.countdownUrgent : ""}`}>{countdownStr}</span>
          )}
          {validDraw && drawExpired && <span className={styles.drawClosed}>{t("qbDrawClosed", "Cerrado")}</span>}
        </div>

        {/* Amount input */}
        <div className={styles.amountSection}>
          <label
            className={styles.amountLabel}
            htmlFor="qb-amount"
          >
            {t("qbAmountLabel", "Monto a apostar")}
          </label>
          <div className={styles.amountInputWrap}>
            <span className={styles.amountCurrency}>Bs.</span>
            <input
              ref={inputRef}
              id="qb-amount"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className={styles.amountInput}
              value={amount}
              onChange={handleAmountChange}
              placeholder="0"
              disabled={drawExpired}
              autoComplete="off"
            />
          </div>
          {amountError && <span className={styles.amountError}>{amountError}</span>}
          <span className={styles.amountRange}>
            Min: {minBet} Bs — Max: {maxBet} Bs
          </span>
        </div>

        {/* Quick chips */}
        <div className={styles.chipsRow}>
          {amountChips.map(v => (
            <button
              key={v}
              type="button"
              className={`${styles.chip} ${numericAmount === v ? styles.chipActive : ""}`}
              onClick={() => handleChipClick(v)}
              disabled={drawExpired}
            >
              {v.toLocaleString("es-VE")}
            </button>
          ))}
        </div>

        {/* Confirm button */}
        <button
          type="button"
          className={styles.confirmBtn}
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          {isPending ? (
            <span className={styles.btnSpinner} />
          ) : (
            <>
              {t("qbConfirm", "Confirmar compra")}
              {numericAmount > 0 && ` — Bs. ${numericAmount.toLocaleString("es-VE")}`}
            </>
          )}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default QuickBuyModal;
